"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { ExternalLink, Hourglass, Info, Loader2, RefreshCw, TriangleAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { Select, type SelectOption } from "@/components/ui/select";
import { api, type Agent, type LiveAvatarRow } from "@/lib/api";

/** Free LiveAvatar sandbox avatar (only id accepted with is_sandbox=true). */
export const LIVEAVATAR_SANDBOX_AVATAR_ID = "dd73ea75-1218-4ef3-92ce-606d5f7fbc0a";

type Props = {
  draft: Agent;
  onChange: (next: Agent) => void;
};

function isDeployingAvatarStatus(raw: string | null | undefined): boolean {
  const s = String(raw || "").trim().toLowerCase();
  return (
    s === "deploying" ||
    s === "pending" ||
    s === "processing" ||
    s === "provisioning" ||
    s === "creating" ||
    s === "in_progress" ||
    s === "in-progress"
  );
}

/** LiveAvatar catalog: only ACTIVE rows are selectable. */
function isActiveAvatarStatus(raw: string | null | undefined): boolean {
  return String(raw || "").trim().toUpperCase() === "ACTIVE";
}

function statusLabel(
  status: string | null | undefined,
  enabled: boolean,
  selectedDeploying: boolean,
): string {
  if (selectedDeploying) return "Deploying";
  if (!enabled) return "Off";
  const s = String(status || "none").toLowerCase();
  if (s === "ready") return "Ready";
  if (s === "provisioning") return "Provisioning";
  if (s === "failed") return "Failed";
  return "Needs avatar";
}

function isMissingApiKeyError(msg: string | null | undefined): boolean {
  const m = String(msg || "").toLowerCase();
  return m.includes("api key not configured") || m.includes("missing secret");
}

function avatarInitials(name: string): string {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "LA";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

export function AgentEditVideoAvatarPanel({ draft, onChange }: Props) {
  const [avatars, setAvatars] = useState<LiveAvatarRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [binding, setBinding] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [listHint, setListHint] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  function patch(partial: Partial<Agent>) {
    onChange({ ...draft, ...partial });
  }

  const loadAvatars = useCallback(async () => {
    setLoading(true);
    setListError(null);
    setListHint(null);
    try {
      const res = await api.listLiveAvatars({ page: 1, pageSize: 100 });
      setAvatars(Array.isArray(res.avatars) ? res.avatars : []);
      const pe = res.partialErrors;
      if (pe?.preset && !pe?.custom) {
        setListHint("Preset catalog unavailable; showing Custom avatars only.");
      } else if (pe?.custom && !pe?.preset) {
        setListHint("Custom avatars unavailable; showing Preset catalog only.");
      }
    } catch (e) {
      setAvatars([]);
      setListError(e instanceof Error ? e.message : "Could not list LiveAvatar avatars");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAvatars();
  }, [loadAvatars]);

  const hasDeploying = avatars.some((a) => isDeployingAvatarStatus(a.status));

  useEffect(() => {
    if (!hasDeploying) return;
    const t = window.setInterval(() => {
      void loadAvatars();
    }, 12000);
    return () => window.clearInterval(t);
  }, [hasDeploying, loadAvatars]);

  function applySaved(saved: Agent) {
    onChange({
      ...draft,
      videoAvatarEnabled: saved.videoAvatarEnabled === true,
      liveAvatarSandbox: saved.liveAvatarSandbox === true,
      liveAvatarId: saved.liveAvatarId || null,
      liveAvatarStatus: saved.liveAvatarStatus || "none",
      liveAvatarError: saved.liveAvatarError || null,
    });
  }

  async function bindAvatar(
    liveAvatarId: string,
    opts?: { enabled?: boolean; sandbox?: boolean },
  ) {
    if (!draft.agentId) return;
    setBinding(true);
    setActionError(null);
    try {
      const saved = await api.bindAgentLiveAvatar(draft.agentId, {
        liveAvatarId,
        videoAvatarEnabled:
          opts?.enabled !== undefined ? opts.enabled : draft.videoAvatarEnabled === true,
        liveAvatarSandbox:
          opts?.sandbox !== undefined ? opts.sandbox : draft.liveAvatarSandbox === true,
      });
      applySaved(saved);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Bind failed");
    } finally {
      setBinding(false);
    }
  }

  async function onToggle(nextEnabled: boolean) {
    const selected = avatars.find((a) => a.id === draft.liveAvatarId);
    if (nextEnabled && (isDeployingAvatarStatus(selected?.status) || draft.liveAvatarStatus === "provisioning")) {
      return;
    }
    if (!draft.agentId) {
      patch({ videoAvatarEnabled: nextEnabled });
      return;
    }
    if (nextEnabled && !draft.liveAvatarId) {
      patch({ videoAvatarEnabled: true, liveAvatarStatus: "none" });
      return;
    }
    setBinding(true);
    setActionError(null);
    try {
      const saved = await api.bindAgentLiveAvatar(draft.agentId, {
        videoAvatarEnabled: nextEnabled,
        liveAvatarId: draft.liveAvatarId || "",
        liveAvatarSandbox: draft.liveAvatarSandbox === true,
      });
      applySaved(saved);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Update failed");
    } finally {
      setBinding(false);
    }
  }

  async function onToggleSandbox(nextSandbox: boolean) {
    if (!draft.agentId) {
      patch({ liveAvatarSandbox: nextSandbox });
      return;
    }
    setBinding(true);
    setActionError(null);
    try {
      const wantId =
        nextSandbox && !draft.liveAvatarId
          ? LIVEAVATAR_SANDBOX_AVATAR_ID
          : draft.liveAvatarId || "";
      const saved = await api.bindAgentLiveAvatar(draft.agentId, {
        liveAvatarSandbox: nextSandbox,
        liveAvatarId: wantId,
        videoAvatarEnabled: draft.videoAvatarEnabled === true,
      });
      applySaved(saved);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Update failed");
    } finally {
      setBinding(false);
    }
  }

  const enabled = draft.videoAvatarEnabled === true;
  const sandbox = draft.liveAvatarSandbox === true;
  const selectedAvatar = avatars.find((a) => a.id === draft.liveAvatarId) || null;
  const selectedIsSandboxAvatar =
    String(draft.liveAvatarId || "").toLowerCase() === LIVEAVATAR_SANDBOX_AVATAR_ID;
  const selectedDeploying = isDeployingAvatarStatus(
    selectedAvatar?.status || (draft.liveAvatarStatus === "provisioning" ? "deploying" : ""),
  );
  const status = statusLabel(draft.liveAvatarStatus, enabled, selectedDeploying);
  const enableLocked = selectedDeploying;
  const sandboxAvatarMismatch = sandbox && Boolean(draft.liveAvatarId) && !selectedIsSandboxAvatar;
  const missingKey =
    isMissingApiKeyError(listError) || isMissingApiKeyError(actionError);

  const selectOptions: SelectOption[] = useMemo(() => {
    const rows = [...avatars];
    if (!rows.some((a) => a.id === LIVEAVATAR_SANDBOX_AVATAR_ID)) {
      rows.unshift({
        id: LIVEAVATAR_SANDBOX_AVATAR_ID,
        name: "LiveAvatar Sandbox (free test)",
        type: "sandbox",
        status: "ACTIVE",
        source: "preset",
      });
    }
    const opts = rows.map((a) => {
      const deploying = isDeployingAvatarStatus(a.status);
      const isSandbox = a.id === LIVEAVATAR_SANDBOX_AVATAR_ID;
      const group = isSandbox
        ? "Sandbox"
        : a.source === "custom"
          ? "Custom Avatars"
          : "Preset Avatars";
      const meta = [a.type || "avatar", a.status, a.id.slice(0, 8)].filter(Boolean).join(" · ");
      const selectable = isActiveAvatarStatus(a.status) && a.isExpired !== true;
      return {
        value: a.id,
        label: a.name,
        description: meta,
        group,
        badge: isSandbox ? "Sandbox" : a.source === "custom" ? "Custom" : "Preset",
        disabled: !selectable,
        suffix: deploying ? (
          <Hourglass
            className="pulse-select-hourglass"
            size={13}
            strokeWidth={1.75}
            aria-label="Deploying"
          />
        ) : undefined,
        avatar: {
          initials: avatarInitials(a.name),
          src: a.previewUrl || undefined,
        },
      } satisfies SelectOption;
    });
    // Sandbox group, then Custom, then Preset.
    const rank = (g: string | undefined) =>
      g === "Sandbox" ? 0 : g === "Custom Avatars" ? 1 : 2;
    return opts.sort((a, b) => {
      const ga = rank(a.group);
      const gb = rank(b.group);
      if (ga !== gb) return ga - gb;
      return a.label.localeCompare(b.label);
    });
  }, [avatars]);

  return (
    <div className="agent-conv agent-video-avatar">
      <section className="agent-conv-block">
        <div className="agent-guards-head">
          <h3 className="agent-conv-h">Video avatar</h3>
          <p className="agent-conv-guards-lead">
            LiveAvatar LITE face on Cartesia voice. Org API key once, then pick an avatar.
          </p>
        </div>

        <div className="agent-conv-switch-row">
          <span className="agent-conv-switch-copy">
            <b>Enable video avatar</b>
            <span className="agent-video-avatar-status">
              Status: {status}
              {selectedDeploying ? (
                <Hourglass className="pulse-select-hourglass" size={12} strokeWidth={1.75} />
              ) : null}
              {draft.liveAvatarId ? ` · ${draft.liveAvatarId.slice(0, 8)}…` : ""}
            </span>
          </span>
          <Switch
            checked={enabled && !enableLocked}
            disabled={binding || missingKey || enableLocked}
            onCheckedChange={(v) => void onToggle(v)}
          />
        </div>
        <div className="agent-conv-switch-row" style={{ marginTop: 10 }}>
          <span className="agent-conv-switch-copy">
            <b>Sandbox</b>
            <span>
              Free short LiveAvatar sessions. Only the Sandbox avatar works here —
              Preset and Custom need this off (billed minutes).
            </span>
          </span>
          <Switch
            checked={sandbox}
            disabled={binding || missingKey}
            onCheckedChange={(v) => void onToggleSandbox(v)}
          />
        </div>
        {sandboxAvatarMismatch ? (
          <div className="agent-conv-callout is-warn" role="status" style={{ marginTop: 10 }}>
            <TriangleAlert size={14} strokeWidth={1.75} aria-hidden />
            <p>
              Preset and Custom need <b>Sandbox</b> off. Keep Sandbox on only with the
              Sandbox avatar (free test).
            </p>
          </div>
        ) : null}

        {draft.liveAvatarError && !missingKey ? (
          <p className="agent-conv-looks-bad" style={{ marginTop: 8 }}>
            {draft.liveAvatarError}
          </p>
        ) : null}
        {actionError && !missingKey ? (
          <p className="agent-conv-looks-bad" style={{ marginTop: 8 }}>
            {actionError}
          </p>
        ) : null}

        {missingKey ? (
          <div className="agent-conv-callout" role="note">
            <Info size={14} strokeWidth={1.75} aria-hidden />
            <p>
              Add a LiveAvatar <b>API key</b> under{" "}
              <Link to="/settings/keys">Settings → API keys</Link>
              {" "}(provider <code>liveavatar</code>). After that, this tab loads Custom
              and Preset avatars to pick from.
            </p>
          </div>
        ) : null}
      </section>

      {!missingKey ? (
        <section className="agent-conv-block">
          <div className="agent-video-avatar-head">
            <div className="agent-guards-head">
              <h3 className="agent-conv-h">Avatar</h3>
            </div>
            <button
              type="button"
              className="btn small"
              disabled={loading || binding}
              onClick={() => void loadAvatars()}
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Refresh
            </button>
          </div>

          {listError ? (
            <p className="agent-conv-looks-bad">{listError}</p>
          ) : null}
          {listHint ? (
            <p className="muted" style={{ marginTop: 6 }}>{listHint}</p>
          ) : null}

          <div className="agent-video-avatar-pick" style={{ marginTop: 10 }}>
            <div className="agent-video-avatar-pick-main">
              {loading && !avatars.length ? (
                <p className="muted">Loading avatars…</p>
              ) : null}
              {!loading && !listError && !avatars.length ? (
                <p className="muted">
                  No avatars returned. Check the LiveAvatar key, or create a Custom avatar in the{" "}
                  <a href="https://app.liveavatar.com" target="_blank" rel="noreferrer">
                    LiveAvatar dashboard <ExternalLink size={12} style={{ display: "inline" }} />
                  </a>
                  , then Refresh.
                </p>
              ) : null}
              {avatars.length ? (
                <Select
                  label="Avatar"
                  groupBy
                  searchable
                  clearable
                  disabled={binding || loading}
                  placeholder="Select a Custom or Preset avatar…"
                  searchPlaceholder="Search avatars…"
                  emptyText="No matching avatars"
                  value={draft.liveAvatarId || null}
                  onChange={(id) => {
                    if (!id) {
                      void bindAvatar("", { enabled });
                      return;
                    }
                    const row = avatars.find((a) => a.id === id);
                    const isSandboxAvatar = id === LIVEAVATAR_SANDBOX_AVATAR_ID;
                    // Synthetic Sandbox row is ACTIVE; catalog rows must be ACTIVE.
                    if (!isSandboxAvatar && !isActiveAvatarStatus(row?.status)) return;
                    if (row?.isExpired === true) return;
                    void bindAvatar(id, {
                      enabled: enabled || true,
                      // Selecting the free sandbox avatar turns Sandbox on; other picks keep current.
                      sandbox: isSandboxAvatar ? true : sandbox,
                    });
                  }}
                  options={selectOptions}
                  footerHint="Sandbox = free test avatar · Custom = account · Preset = public catalog"
                />
              ) : null}
            </div>
            {selectedAvatar?.previewUrl ? (
              <div className="agent-video-avatar-preview">
                <img
                  src={selectedAvatar.previewUrl}
                  alt=""
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
                <span className="agent-video-avatar-preview-cap" title={selectedAvatar.name}>
                  {selectedAvatar.name}
                </span>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}
    </div>
  );
}
